import React, { useState } from "react";

import api from "../services/api";

const btnStyle = {
  padding: "8px 14px",
  cursor: "pointer",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  fontSize: 13,
  fontWeight: 600,
};

// Player da gravação de uma chamada + download do PCAP do SIP. Ambos carregam
// sob demanda (com auth, via blob) e são independentes: uma chamada pode ter
// PCAP sem gravação (o áudio só existe se o fluxo gravou) e vice-versa, então
// cada botão trata o próprio "indisponível" em vez de sumir junto com o outro.
const RecordingPlayer = ({ callSid, applicationId, publicUrl }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [pcapLoading, setPcapLoading] = useState(false);
  const [pcapErr, setPcapErr] = useState(false);
  const [pubLoading, setPubLoading] = useState(false);
  const [pubMsg, setPubMsg] = useState(null); // { text, error }
  const [copied, setCopied] = useState(false);

  if (!callSid) return null;

  const load = async () => {
    if (url || loading) return;
    setLoading(true);
    setErr(false);
    try {
      const { data } = await api.get(`/recordings/${callSid}`, { responseType: "blob" });
      setUrl(URL.createObjectURL(data));
    } catch (e) {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  const baixarPcap = async () => {
    if (pcapLoading) return;
    setPcapLoading(true);
    setPcapErr(false);
    try {
      const { data } = await api.get(`/pcap/${callSid}`, { responseType: "blob" });
      // dispara o download com um <a> temporário (o blob não tem URL própria)
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${callSid}.pcap`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // revoga depois do clique — antes disso o navegador ainda não leu o blob
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch (e) {
      setPcapErr(true);
    } finally {
      setPcapLoading(false);
    }
  };

  // Gera o link PÚBLICO assinado desta gravação (o backend só libera se o gatilho
  // "Gravar chamada" do fluxo tem "Tornar pública" ligado) e copia pra área de
  // transferência. Só aparece quando sabemos de qual fluxo é a gravação.
  const gerarLinkPublico = async () => {
    if (pubLoading || !applicationId) return;
    setPubLoading(true);
    setPubMsg(null);
    try {
      const { data } = await api.get(`/recordings/${callSid}/public-link`, {
        params: { applicationId },
      });
      const full = `${window.location.origin}${data.path}`;
      try {
        await navigator.clipboard.writeText(full);
        setPubMsg({ text: "Link público copiado para a área de transferência.", error: false });
      } catch {
        // clipboard bloqueado (http/permite): mostra o link pro usuário copiar à mão
        setPubMsg({ text: full, error: false });
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.error) ||
        "Não foi possível gerar o link público.";
      setPubMsg({ text: msg, error: true });
    } finally {
      setPubLoading(false);
    }
  };

  // Link público já pronto (veio na SAÍDA do nó, gerado no ingest): exibe direto,
  // com botão copiar — sem precisar gerar sob demanda. Tem precedência sobre o botão.
  const copyReady = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard bloqueado: o link já está visível pra copiar à mão */
    }
  };

  const publicLinkReady = publicUrl ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: "100%" }}>
      <span style={{ fontSize: 12.5, color: "#5F5E5A" }}>🔗 Link público da gravação:</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: "#1565C0", wordBreak: "break-all" }}>
          {publicUrl}
        </a>
        <button
          type="button"
          onClick={copyReady}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12.5, color: "#2E7D32", textDecoration: "underline", textUnderlineOffset: 2 }}
        >
          {copied ? "copiado ✓" : "copiar"}
        </button>
      </div>
    </div>
  ) : null;

  const publicLinkBtn = !publicUrl && applicationId ? (
    <button
      type="button"
      onClick={gerarLinkPublico}
      disabled={pubLoading}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: pubLoading ? "default" : "pointer",
        fontSize: 12.5,
        color: "#5F5E5A",
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
      title="Gera um link assinado da gravação, acessível pela internet sem login (se o fluxo permitir)"
    >
      {pubLoading ? "Gerando link…" : "🔗 Link público da gravação"}
    </button>
  ) : null;

  // Link DISCRETO de propósito: só o suporte técnico de telefonia usa a captura
  // SIP, então ela não deve competir com o player, que é o que o usuário comum
  // quer. Sem borda, sem fundo, tipografia menor.
  const pcapLink = (
    <button
      type="button"
      onClick={baixarPcap}
      disabled={pcapLoading}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: pcapLoading ? "default" : "pointer",
        fontSize: 12.5,
        color: pcapErr ? "#B26A00" : "#5F5E5A",
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
      title="Arquivo com a sinalização SIP da chamada — para diagnóstico de telefonia"
    >
      {pcapLoading
        ? "Baixando captura…"
        : pcapErr
        ? "Captura indisponível — tentar de novo"
        : "Baixar captura da chamada"}
      {!pcapLoading && !pcapErr && (
        <span style={{ color: "#9A9891", textDecoration: "none", marginLeft: 6 }}>.pcap</span>
      )}
    </button>
  );

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
      {url ? (
        <audio controls autoPlay src={url} style={{ width: "100%" }} />
      ) : (
        <button type="button" onClick={load} disabled={loading} style={btnStyle}>
          {loading ? "Carregando gravação…" : err ? "Gravação indisponível — tentar de novo" : "▶ Ouvir gravação"}
        </button>
      )}
      {publicLinkReady}
      {publicLinkBtn}
      {pubMsg && (
        <div
          style={{
            fontSize: 12.5,
            color: pubMsg.error ? "#B26A00" : "#2E7D32",
            wordBreak: "break-all",
            maxWidth: "100%",
          }}
        >
          {pubMsg.text}
        </div>
      )}
      {pcapLink}
    </div>
  );
};

export default RecordingPlayer;
